import { Component, Host, h, State, Element, Prop, Watch } from '@stencil/core';
import { closestAll } from '../../utils/domHelpers';
import tinycolor from 'tinycolor2';

export interface AhaA11yTextColor extends HTMLAhaA11yTextColorElement {}

/** A component which renders WCAG 2.1 readable text against on any element with a colored background.
 *
 * This component calculates its text color based on the `backgroundColor` of the closest element with a defined `backgroundColor` value. This `backgroundColor` value can be set either in-line as a `style` attribute or can come from an external CSS file.
 *
 * ### Rules
 *
 * A few simple rules govern how this component colors and displays text:
 *
 * 1. If the original text color has WCAG compliant contrast with the background element, no change to the text color will be made.
 *
 * 2. If the original text color is not WCAG contrast compliant, then the component will alter the text color to ensure compliance. The changes to the original color will be as minimal as possible.
 *
 * 3. If _no_ value of colored text is considered to be WCAG compliant (this can occasionally happen for very saturated colors like `green`), then the _most readable color_ will be chosen and will be either white or black, depending on the circumstances.
 *
 * ### Usage notes
 *
 * The `<aha-a11y-text-color>` component will work correctly _only_ if there is a parent element with a solid background color (no gradients, images, etc.).
 *
 * The `<aha-a11y-text-color>` element will automatically monitor its ancestor elements. If one of those elements has a `class`, `id`, `style`, or any other attribute change, then this is taken as sufficient evidence that a re-render of the `<aha-a11y-text-color>` element is necessary, as any of those changes could have caused the text to be on a different background color.
 *
 * If, for some reason, you need to manually trigger a re-render, simply get the element and call its `refresh()` function.
 *
 *
 */
@Component({
  tag: 'aha-a11y-text-color',
  shadow: true,
})
export class AhaA11yTextColor {
  @Element() host!: HTMLAhaA11yTextColorElement;

  /** The WCAG readability conformance level. https://www.w3.org/TR/WCAG21 */
  @Prop({ reflect: true }) level: 'AA' | 'AAA' = 'AA';

  /** Whether or not the text color should be a shade of the ancestor element's background color.
   *
   * If set to `true`, then the `<aha-a11y-text-color>` initial text color will be ignored, and the text color will be based on the ancestor element's background color.
   *
   * If set to `false` then the initial text color will be the base on which the accessible text color will be built on. In other words, the text's original color will be preserved as much as possible.
   *
   * **Example:**
   *
   * With `shade` set to `false` white text on a pink background will become gray text.
   *
   * With `shade` set to `true` white text on a pink background will become a reddish color.
   */
  @Prop({ reflect: true }) shade: boolean = false;

  @State() color: string | undefined = undefined;
  private backgroundColor: any = undefined;
  private ancestorMutationObservers: Set<MutationObserver> = new Set();

  @Watch('level')
  @Watch('shade')
  private refresh() {
    // Reset initial values
    this.color = undefined;
    this.backgroundColor = undefined;

    let color;

    const allParents = closestAll(this.host);
    for (let i = 0; i < allParents.length; i++) {
      const ancestor = allParents[i];
      const styles = getComputedStyle(ancestor);
      const backgroundColor = styles.backgroundColor;

      // The first ancestor is always the host element. We only capture the color value here.
      if (!color) color = styles.color;

      // `backgroundColor` will be an `rgba` value, and so we must check to see if the alpha value is greater than zero.
      if (!this.backgroundColor && backgroundColor && tinycolor(backgroundColor).getAlpha() > 0) {
        // Because we are trying to find the minimum readable color for the WCAG level, we assume that
        // both the `color` and the `backgroundColor` have the same value.
        this.backgroundColor = backgroundColor;
        if (this.shade) color = this.backgroundColor;
      }
    }

    if (color && this.backgroundColor) {
      if (
        !tinycolor.isReadable(color, this.backgroundColor, {
          level: this.level,
        })
      ) {
        // The method by which we should get our readable shade
        const method = tinycolor(this.backgroundColor).isLight() ? 'darken' : 'lighten';
        const tempColor = tinycolor(color);

        // We want to examine 100 possible shades of the background color to find the color that is
        // readable yet is still as close as possible to the original background color.
        let steps = [...Array(100).keys()];

        // Bisect the possible values and quickly filter to find the closest readable color.
        while (steps.length > 1) {
          const midpoint = Math.floor(steps.length / 2);
          const arr1 = steps.slice(0, midpoint);
          const arr2 = steps.slice(midpoint);

          const step1 = arr1.at(-1);
          const step2 = arr2.at(0);

          const step1Shade = tempColor.clone()[method](step1);
          const step2Shade = tempColor.clone()[method](step2);

          const step1Readable = tinycolor.isReadable(step1Shade, this.backgroundColor, {
            level: this.level,
          });

          const step2Readable = tinycolor.isReadable(step2Shade, this.backgroundColor, {
            level: this.level,
          });

          if (step1Readable && step2Readable) {
            // Both shades are readable.
            const step1Contrast = tinycolor.readability(step1Shade, this.backgroundColor);
            const step2Contrast = tinycolor.readability(step2Shade, this.backgroundColor);
            // Pick the array with the _lowest_ contrast ratio (as that is closest to the original).
            steps = step1Contrast < step2Contrast ? arr1 : arr2;
          } else if (!step1Readable && !step2Readable) {
            // Neither shade is readable.
            const step1Contrast = tinycolor.readability(step1Shade, this.backgroundColor);
            const step2Contrast = tinycolor.readability(step2Shade, this.backgroundColor);
            // Pick the array with the _highest_ contrast ratio (as that is most likely to have a readable value).
            steps = step1Contrast < step2Contrast ? arr2 : arr1;
          } else {
            // Pick the array with the readable color.
            steps = step1Readable ? arr1 : arr2;
          }
        }

        this.color = tempColor.clone()[method](steps[0]).toString('rgb');
      }

      // No color has returned as readable (this can happen with certain colors, for example "green").
      // In this case, pick the _best possible_ color for the text (either white or black),
      // given the background.
      if (!this.color) {
        this.color = tinycolor.mostReadable(this.backgroundColor, ['#fff', '#000']).toString('rgb');
      }
    }
  }

  componentDidLoad() {
    // Colorize the text the first render.
    this.refresh();

    // Set up mutation obsevrers on all ancestor elements. Why do we need to do this? Because it's possible that a parent element's
    // background color is more than just the color you think you see.
    //
    // For example, let's image we are trying to get the WCAG color for text inside a colored box. This box is an error box, and has
    // a pinkish background. But if the user is in dark mode, the error box will actually be a dark red. What determins whether or not
    // the box is red or pink isn't the box itself, rather it is a class name on the root <html> or <body> elements. The color is
    // determined by the _combination_ of the actual background color on the parent element and the class on a node far up the DOM
    // heirarchy. If the class name on the <html> element changes, then we need to know about that or our text will be the wrong color
    // and will no longer be WCAG compliant. The only way to do that is to set up an attribute mutation observer on the ancestor elements.
    //
    // If _any_ attribute of _any_ ancestor element changes, it could be enough to warrant a refresh of the text color, so refresh the
    // text just to be on the safe side.
    const that = this;
    const ancestors = closestAll(this.host, '*') as HTMLElement[];
    ancestors.forEach(ancestor => {
      const obs = new MutationObserver(() => {
        that.refresh();
      }) as MutationObserver;
      obs.observe(ancestor, { attributes: true });
      this.ancestorMutationObservers.add(obs);
    });
  }

  disconnectedCallback() {
    // Clean up mutation observers on ancestor elements.
    this.ancestorMutationObservers.forEach(obs => {
      obs.disconnect();
    });
    this.ancestorMutationObservers.clear();
  }

  render() {
    return (
      <Host>
        <span style={{ color: this.color }}>
          <slot></slot>
        </span>
      </Host>
    );
  }
}
